import * as yup from "yup";
import "yup-phone-lite";
import { isValidPhoneNumber } from "libphonenumber-js";
import { subDays } from "date-fns";
import { clearSecondsAndMilliseconds, hasSequentialChars } from "../../utils/utils";

// ========================================
// REUSABLE VALIDATORS
// ========================================
const hostname = globalThis.location.hostname;
const isProduction = hostname === 'dashboard.myhealthchecked.com';

const emailValidation = yup
  .string()
  .required("Please enter your email address")
  .email("Please enter a valid email address");

const phoneValidation = yup
  .string()
  .when([], {
    is: () => isProduction,
    then: (schema) => schema.phone("GB", "Please enter a valid UK phone number"),
    otherwise: (schema) => schema,
  })
  .required("Phone number is required");

const passwordValidation = yup
  .string()
  .required("Password is required")
  .matches(/^[^ ]*$/, "Password must not contain any spaces")
  .matches(/^(?!.*(.)(?:\1){2,})/i, "Password must not contain repetitive characters")
  .test(
    "no-sequential-chars",
    "Password should not contain sequences of three or more consecutive letters or numbers",
    (value) => !hasSequentialChars(value || ""),
  )
  .matches(/^(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
  .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
  .matches(
    /^(?=.*[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`"'\\])/,
    "Password must contain at least one special character",
  )
  .matches(/^(?=.*\d)/, "Password must contain at least one number")
  .min(8, "Minimum 8 characters");

const identify_with_gender = yup
  .string()
  .required("You must indicate if you still identify with this gender");

const dobValidation = yup
  .date()
  .required("Please enter a valid date of birth")
  .test("age", "Age must be between 18 and 100 years", (value) => {
    if (!value) return false;
    const today = new Date();
    const dob = new Date(value);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 18 && age <= 100;
  });

const sexValidation = yup
  .string()
  .required("Sex at birth is required")
  .test("not-pnts", "Sex at birth is required", function (value) {
    return !(value === "pnts" && !this.parent.isDnaTest);
  });

// ========================================
// REUSABLE FIELD GROUPS
// ========================================

const nameValidation = (fieldName) =>
  yup
    .string()
    .required(`Please enter your ${fieldName}`)
    .test(
      "no-emoji-symbol",
      "Please remove special characters or emojis",
      (value) => !hasEmojiOrSymbol(value ?? "")
    )
    .matches(
      /^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/,
      "Please remove special characters or emojis"
    );


// ─── DOB Validation
const hasEmojiOrSymbol = (value) =>
  /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}\u{2B00}-\u{2BFF}]/u.test(
    value ?? ""
  );
const dobValidationNew = yup
  .string()
  .required("Please enter your date of birth")
  .test(
    "no-emoji-symbol",
    "Enter a valid date of birth",
    (value) => !hasEmojiOrSymbol(value ?? "")
  )
  .matches(/^[0-9/.-]+$/, "Enter a valid date of birth")
  .test("is-valid-date", "Enter a valid date of birth", (value) => {
    if (!value) return false;

    const date = new Date(value);
    return !Number.isNaN(date.getTime());
  })
  .test("age-range", "Age must be between 18 and 100 years", (value) => {
    if (!value) return false;
    const dob = new Date(value);
    if (Number.isNaN(dob.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dob.getDate())
    ) {
      age--;
    }
    return age >= 18 && age <= 100;
  });

const getCountryName = (iso2) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(iso2.toUpperCase());
  } catch {
    return iso2.toUpperCase();
  }
};

// ─── Telephone Validation
// FormInput stores the rendered field type as `<field>_type` and, for the
// IntlTelInput field, the selected country as `<field>_iso2`.
const UK_MOBILE_REGEX = /^(?:\+44\s?7\d{3}|\(?07\d{3}\)?|7\d{3})\s?\d{3}\s?\d{3}$/;

function isAdvancePhone(ctx) {
  return ctx.parent?.[`${ctx.path}_type`] === "advancePhoneNumber";
}

const telephoneValidation = yup
  .string()
  .required("Please enter your mobile number")
  // type "phone" (UK field)
  .test("digits-only", "Phone number must contain digits only", function (value) {
    if (isAdvancePhone(this) || !value) return true;
    return /^[\d ]+$/.test(value);
  })
  .test("max-length", "Phone number must be at most 13 digits", function (value) {
    if (isAdvancePhone(this) || !value) return true;
    return value.replace(/ /g, "").length <= 13;
  })
  .test("uk-mobile", "Enter a valid UK mobile number, e.g. 07XXX XXXXXX.", function (value) {
    if (isAdvancePhone(this) || !value) return true;
    return UK_MOBILE_REGEX.test(value);
  })
  .test("no-emoji-symbol", "Enter a valid telephone number", function (value) {
    if (isAdvancePhone(this)) return true;
    return !hasEmojiOrSymbol(value ?? "");
  })
  // type "advancePhoneNumber" (IntlTelInput field)
  .test("valid-for-country", function (value) {
    if (!isAdvancePhone(this) || !value) return true;
    const iso2 = this.parent?.[`${this.path}_iso2`];
    if (!iso2) return true;
    if (isValidPhoneNumber(value, iso2.toUpperCase())) return true;
    return this.createError({
      message: `Please enter a valid ${getCountryName(iso2)} phone number`,
    });
  });

const commonFields = {
  first_name: nameValidation("first name"),
  last_name: nameValidation("last name"),
  dob: dobValidationNew,
  sex: sexValidation,
  ethnicity: yup.string().required("Ethnicity is required"),
};

// Address fields reused across multiple schemas
const addressFields = {
  home_postal_code: yup.string().required("Please enter a valid postal code"),
  home_street_address: yup.string().required("Please enter a valid home address"),
  address_line_1: yup
    .string()
    .min(3, "Address line 1 must be at least 3 characters")
    .when(["home_street_address"], ([home_street_address], schema) =>
      home_street_address === "No results"
        ? schema.required("Please enter a valid address line")
        : schema.notRequired(),
    ),
  home_city: yup
    .string()
    .when(["home_street_address"], ([home_street_address], schema) =>
      home_street_address === "No results"
        ? schema.required("Please enter a valid city name")
        : schema.notRequired(),
    ),
  home_country: yup.string().max(3, "Home country must be at most 3 characters").notRequired(),
};

// Conditional "when Yes" helper
const whenYes = (field, message) =>
  yup.string().when(field, ([val], schema) =>
    val === "Yes" ? schema.required(message) : schema.nullable(),
  );

const whenYesArray = (field, message) =>
  yup.array().when(field, ([val], schema) =>
    val === "Yes"
      ? schema.min(1, message).required(message)
      : schema.notRequired(),
  );

// ========================================
// SCHEMAS
// ========================================

export const loginSchema = yup.object().shape({
  email: emailValidation,
  password: yup.string().required("Please enter your password"),
});

export const mfaSchema = yup.object().shape({
  primary_contact_no: telephoneValidation,
});
export const optSchema = yup.object().shape({
  verifyOtp: yup
    .string()
    .required("OTP is required")
    .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
});
export const resetPassSchema = yup.object().shape({
  email: emailValidation,
  recaptcha_token: yup.string().required("reCAPTCHA is required"),
});

export const setPasswordSchema = yup.object().shape({
  password: passwordValidation,
  confirm_password: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

export const createAccountSchema = yup.object().shape({
  ...commonFields,
  email: emailValidation
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, "Please enter a valid email address")
    .email("Please enter a valid email"),
  password: passwordValidation,
  ...addressFields,
  phone_number_primary: telephoneValidation,
  sex: sexValidation,
  identify_with_gender,
  service_consent: yup
    .boolean()
    .oneOf([true], "You must consent to terms and conditions")
    .required("You must consent to testing services"),
  policy_consent: yup
    .boolean()
    .oneOf([true], "You must consent to privacy policy")
    .required("You must consent to testing services"),
  marketing_consent: yup.boolean(),
  recaptcha_token: yup.string().required("reCAPTCHA is required"),
});

export const personalDetailsSchema = yup.object().shape({
  ...commonFields,
  sex: sexValidation,
  identify_with_gender,
  phone_number_primary: telephoneValidation,
  ...addressFields,
});

export const personalDetailsShSchema = yup.object().shape({
  ...commonFields,
  sex: sexValidation,
  identify_with_gender,
  phone_number_primary: telephoneValidation,
  ...addressFields,
});

export const personalDetailsDnaSchema = yup.object().shape({
  ...commonFields,
  sex: sexValidation,
  identify_with_gender,
  phone_number_primary: telephoneValidation,
  ...addressFields,
});

export const editDetailsSchema = yup.object().shape({
  ...commonFields,
  identify_with_gender,
  phone_number_primary: telephoneValidation,
  phone_number_secondary: yup
    .string()
    .nullable()
    .max(11, "Phone number must be at most 11 characters")
    .matches(/^\d+$/, "Please enter a valid UK phone number")
    .test("valid-phone", "Please enter a valid UK phone number", function (value) {
      if (value && value.trim() !== "") {
        return yup.string().phone("GB").isValidSync(value);
      }
      return true;
    }),
  ...addressFields,
});

export const activationSchema = yup.object().shape({
  email: emailValidation,
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
    )
    .matches(/^[^\s]+$/, "Password must not contain any spaces")
    .matches(/^(?!.*(\w)\1{2,})/, "Password must not contain repetitive characters")
    .matches(
      /^(?!.*(?:abcdefghijklmnopqrstuvwxyz|ABCDEFGHIJKLMNOPQRSTUVWXYZ|01234567890))[\w@$!%*?&]{8,}$/,
      "Password must not contain sequential characters",
    ),
  occupation: yup.string().required("Occupation is required"),
  credentials: yup.string().required("Credentials is required"),
  organisation: yup.string().required("Organisation is required"),
  mobile: phoneValidation,
  name: yup.string("Name cannot be a number").required("Name is required"),
});

export const phlebotomyDetailsSchema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  dob: yup
    .date()
    .required("Date of Birth is required")
    .test("age", "You must be at least 18 years old", (value) =>
      value && new Date().getFullYear() - new Date(value).getFullYear() >= 18,
    ),
  sex: sexValidation,
  identify_with_gender,
  phone_number_primary: phoneValidation.max(14, "Phone number must be at most 14 characters"),
});

// ========================================
// TIME / DATE SCHEMAS
// ========================================

const today = new Date();
const fifteenDaysAgo = subDays(today, 15);

export const timeAdministeredSchema = yup.object().shape({
  sample_collection_date: yup
    .date()
    .required("Date of test taken is required")
    .max(today, "Date cannot be in the future")
    .min(fifteenDaysAgo, "Date cannot be older than 15 days"),
  sample_collection_time: yup
    .string()
    .required("Time of test taken is required")
    .matches(/^([0-1]?\d|2[0-3]):[0-5]\d$/, "Time format must be HH:mm")
    .test(
      "time-within-60-minutes",
      "Timestamp cannot be more than one hour in the future",
      function (value) {
        const { sample_collection_date } = this.parent;
        if (
          sample_collection_date &&
          new Date(sample_collection_date).toDateString() === today.toDateString()
        ) {
          const [hours, minutes] = value.split(":").map(Number);
          let collectionTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
          collectionTime = clearSecondsAndMilliseconds(collectionTime);
          let currentTime = clearSecondsAndMilliseconds(new Date());
          const maxTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
          return collectionTime <= maxTime;
        }
        return true;
      },
    ),
});

// ========================================
// ASSESSMENT SCHEMAS
// ========================================

export const bodyAssessmentSchema = yup.object().shape({
  height: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .required("Height is required")
    .when("heightUnits", ([heightUnits], schema) =>
      heightUnits === "imperial"
        ? schema.min(20, "Height must be greater than or equal to 20 inches").max(118, "Height must be less than or equal to 118 inches")
        : schema.min(50, "Height must be greater than or equal to 50 cms").max(300, "Height must be less than or equal to 300 cms"),
    ),
  weight: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .required("Weight is required")
    .when("weightUnits", ([weightUnits], schema) =>
      weightUnits === "imperial"
        ? schema.min(45, "Weight must be greater than or equal to 45 lbs").max(1102, "Weight must be less than or equal to 1102 lbs")
        : schema.min(20, "Weight must be greater than or equal to 20 kgs").max(500, "Weight must be less than or equal to 500 kgs"),
    ),
  ispregnant: yup.string().nullable().notRequired(),
  trimester: yup
    .string()
    .when("ispregnant", ([ispregnant], schema) =>
      ispregnant === "Yes" ? schema.required("Please select your trimester") : schema.notRequired(),
    ),
  smoking: yup.string().required("Smoking status is required"),
  cigarettes_per_day: whenYes("smoking", "Please provide the number of cigarettes you smoke each day"),
  how_many_year: yup.string().when("smoking", ([smoking], schema) =>
    smoking === "Ex-smoker"
      ? schema.required("Please provide the number of years you smoke")
        .test(
          "greater-than-zero",
          "Years must be greater than 0",
          (value) => Number(value) > 0
        )
      : schema.notRequired(),
  ),
  how_many_day_average: yup.string().when("smoking", ([smoking], schema) =>
    smoking === "Ex-smoker"
      ? schema.required("Please provide the number of cigarettes on average you smoke each day")
        .test(
          "greater-than-zero",
          "Average cigarettes per day must be greater than 0",
          (value) => Number(value) > 0
        )
      : schema.notRequired(),
  ),
  alcohol: yup.string().required("Alcohol consumption status is required"),
  alcohol_units_per_week: whenYes("alcohol", "Please provide your alcohol consumption units each week"),
});

export const exerciseSchema = yup.object().shape({
  physical_activity: yup
    .string()
    .oneOf(["Yes", "No"], "Please select Yes or No")
    .required("Physical activity status is required"),
  light_activity_hours_per_week: yup
    .string()
    .when("physical_activity", ([physical_activity], schema) =>
      physical_activity === "Yes"
        ? schema.required("Light activity hours are required").oneOf(["0", "1", "2", "3", "4", "5", "6", "7", "7+"], "Please select a valid number of hours")
        : schema.notRequired(),
    ),
  moderate_activity_hours_per_week: yup
    .string()
    .when("physical_activity", ([physical_activity], schema) =>
      physical_activity === "Yes"
        ? schema.required("Moderate activity hours are required").oneOf(["0", "1", "2", "3", "4", "5", "6", "7", "7+"], "Please select a valid number of hours")
        : schema.notRequired(),
    ),
  vigorous_activity_hours_per_week: yup
    .string()
    .when("physical_activity", ([physical_activity], schema) =>
      physical_activity === "Yes"
        ? schema.required("Vigorous activity hours are required")
        : schema.notRequired(),
    )
    .oneOf(["0", "1", "2", "3", "4", "5", "6", "7", "7+"], "Please select a valid number of hours"),
});

export const menstrualSchema = yup.object().shape({
  cycle_length: yup.string().required("Please select your normal cycle length"),
  day_of_cycle: yup.string().required("Please select the day of your cycle when this blood test was taken"),
  contraception: yup.string().required("Please select your contraception status"),
});

export const barcodeValidationSchema = (pathname) =>
  yup.object().shape({
    code: yup
      .string()
      .test("is-valid-code", "Invalid barcode format, should be 5 letters followed by 5 digits", function (value) {
        if (pathname?.includes("/enter-barcode/dna")) {
          return /^[A-Z]{5}\d{5}$/.test(value);
        }
        return true;
      })
      .required("Barcode is required"),
    trackingNumber: yup
      .string()
      .matches(/^[A-Z]{2}\s\d{4}\s\d{4}\s\d[A-Z]{2}$/, "Tracking number must be in the format AA 1234 5678 9BB")
      .required("Tracking number is required"),
  });

export const shSchema = yup.object().shape({
  sexually_active: yup.string().required("This field is required"),
  partners: whenYes("sexually_active", "This field is required when sexually active"),
  recent_sex: whenYes("sexually_active", "This field is required when sexually active"),
  had_unprotected_sex: whenYes("sexually_active", "This field is required when sexually active"),
  unprotected_sex_type: yup
    .array()
    .when(["sexually_active", "had_unprotected_sex"], ([sexually_active, had_unprotected_sex], schema) =>
      sexually_active === "Yes" && had_unprotected_sex === "Yes"
        ? schema.min(1, "Please select at least one option if you had unprotected sex").required("Please select at least one option if you had unprotected sex")
        : schema.notRequired(),
    ),
  sti_symptoms: whenYes("sexually_active", "This field is required when sexually active"),
  sti_symptoms_description: whenYes("sti_symptoms", "Please provide details of your symptoms"),
  sti_diagnosed: whenYes("sexually_active", "This field is required when sexually active"),
  potential_sti_exposure: whenYes("sexually_active", "This field is required when sexually active"),
  potential_sti_exposure_type: yup
    .array()
    .when(["potential_sti_exposure", "sexually_active"], ([potential_sti_exposure, sexually_active], schema) =>
      potential_sti_exposure === "Yes" && sexually_active === "Yes"
        ? schema.min(1, "Please select at least one option if you think you may have been exposed to an STI").required("Please select at least one option if you think you may have been exposed to an STI")
        : schema.notRequired(),
    ),
  other_sti_exposure_type: yup
    .string()
    .when(
      ["potential_sti_exposure", "sexually_active", "potential_sti_exposure_type"],
      ([potential_sti_exposure, sexually_active, potential_sti_exposure_type], schema) =>
        potential_sti_exposure === "Yes" && sexually_active === "Yes" && potential_sti_exposure_type.includes("Other")
          ? schema.required("Please provide details of your STI exposure type")
          : schema.notRequired(),
    ),
});

export const dnaAssessmentSchema = yup.object().shape(
  Object.fromEntries(
    ["dairy", "nuts", "gluten", "shellfish", "oily_fish", "eggs", "poultry", "red_meat", "pork", "alcohol"].map(
      (field) => [field, yup.string().required("Field is required")],
    ),
  ),
);

export const typeConfirmationSchema = yup.object().shape({
  test_type: yup.string().required("Test type is required"),
});

// ========================================
// MEDICAL / MEDICATIONS / SUPPLEMENTS
// ========================================

const whenDiagnosedWithCondition = (condition, message) =>
  yup.string().when(["was_diagnosed", "condition"], ([was_diagnosed, cond], schema) =>
    was_diagnosed === "Yes" && cond?.includes(condition)
      ? schema.required(message)
      : schema.notRequired().nullable(),
  );

const whenMedicationIncludes = (med, message) =>
  yup.string().when(["medication", "medications"], ([medication, medications], schema) =>
    medication === "Yes" && medications?.includes(med)
      ? schema.required(message)
      : schema.notRequired().nullable(),
  );

const whenSupplementIncludes = (supplement, message) =>
  yup.string().when(["taking_supplements", "supplements"], ([taking_supplements, supplements], schema) =>
    taking_supplements === "Yes" && supplements?.includes(supplement)
      ? schema.required(message)
      : schema.notRequired().nullable(),
  );

const yesNoDropdown = (fieldName) =>
  yup
    .string()
    .oneOf(["Yes", "No"], "Please select either 'Yes' or 'No' from the dropdown menu")
    .required("Please select either 'Yes' or 'No' from the dropdown menu");

export const medicalConditionsMedicationsSupplementsSchema = yup.object().shape({
  was_diagnosed: yesNoDropdown("was_diagnosed"),
  condition: yup
    .array()
    .when("was_diagnosed", ([was_diagnosed], schema) =>
      was_diagnosed === "Yes"
        ? schema.min(1, "Please select at least one medical condition").required("Please select at least one medical condition")
        : schema.notRequired().nullable(),
    ),
  cancer_type: whenDiagnosedWithCondition("Cancer", "Please specify cancer type and treatment details"),
  deficiency: whenDiagnosedWithCondition("Vitamin/mineral deficiency", "Please specify which deficiency"),
  other_description: whenDiagnosedWithCondition("Other", "Please specify other medical condition"),

  medication: yesNoDropdown("medication"),
  medications: yup
    .array()
    .when("medication", ([medication], schema) =>
      medication === "Yes"
        ? schema.min(1, "Please select at least one medication").required("Please select at least one medication")
        : schema.notRequired().nullable(),
    ),
  specify_medication: whenMedicationIncludes("Cholesterol-lowering", "Please tell us the name, dosage, and frequency of your medication"),
  medical_other_description: whenMedicationIncludes("Other", "Please specify other medication details"),

  taking_supplements: yesNoDropdown("taking_supplements"),
  supplements: yup
    .array()
    .when("taking_supplements", ([taking_supplements], schema) =>
      taking_supplements === "Yes"
        ? schema.min(1, "Please select at least one supplement").required("Please select at least one supplement")
        : schema.notRequired().nullable(),
    ),
  supplement_details: whenSupplementIncludes("Iron, Folate", "Please tell us the name, dosage, and frequency"),
  supplement_other_description: whenSupplementIncludes("Other", "Please specify other supplement details"),
});


export const heightWeightSchema = yup.object().shape({
  height: bodyAssessmentSchema.fields.height,
  weight: bodyAssessmentSchema.fields.weight,
  ispregnant: bodyAssessmentSchema.fields.ispregnant,
  trimester: bodyAssessmentSchema.fields.trimester,
});

export const lifestyleSchema = yup.object().shape({
  smoking: bodyAssessmentSchema.fields.smoking,

  cigarettes_per_day:
    bodyAssessmentSchema.fields.cigarettes_per_day,

  how_many_year:
    bodyAssessmentSchema.fields.how_many_year,

  how_many_day_average:
    bodyAssessmentSchema.fields.how_many_day_average,

  alcohol: bodyAssessmentSchema.fields.alcohol,

  alcohol_units_per_week:
    bodyAssessmentSchema.fields.alcohol_units_per_week,

  physical_activity:
    exerciseSchema.fields.physical_activity,

  light_activity_hours_per_week:
    exerciseSchema.fields.light_activity_hours_per_week,

  moderate_activity_hours_per_week:
    exerciseSchema.fields.moderate_activity_hours_per_week,

  vigorous_activity_hours_per_week:
    exerciseSchema.fields.vigorous_activity_hours_per_week,
});

export const medicalSchema = yup.object().shape({
  // Medical conditions
  was_diagnosed:
    medicalConditionsMedicationsSupplementsSchema.fields
      .was_diagnosed,

  condition:
    medicalConditionsMedicationsSupplementsSchema.fields
      .condition,

  cancer_type:
    medicalConditionsMedicationsSupplementsSchema.fields
      .cancer_type,

  deficiency:
    medicalConditionsMedicationsSupplementsSchema.fields
      .deficiency,

  other_description:
    medicalConditionsMedicationsSupplementsSchema.fields
      .other_description,

  // Medications
  medication:
    medicalConditionsMedicationsSupplementsSchema.fields
      .medication,

  medications:
    medicalConditionsMedicationsSupplementsSchema.fields
      .medications,

  specify_medication:
    medicalConditionsMedicationsSupplementsSchema.fields
      .specify_medication,

  medical_other_description:
    medicalConditionsMedicationsSupplementsSchema.fields
      .medical_other_description,

  // Supplements
  taking_supplements:
    medicalConditionsMedicationsSupplementsSchema.fields
      .taking_supplements,

  supplements:
    medicalConditionsMedicationsSupplementsSchema.fields
      .supplements,

  supplement_details:
    medicalConditionsMedicationsSupplementsSchema.fields
      .supplement_details,

  supplement_other_description:
    medicalConditionsMedicationsSupplementsSchema.fields
      .supplement_other_description,
});
export const dnaAssessmentOnlySchema = yup.object().shape({
  dairy: dnaAssessmentSchema.fields.dairy,

  nuts: dnaAssessmentSchema.fields.nuts,

  gluten: dnaAssessmentSchema.fields.gluten,

  shellfish: dnaAssessmentSchema.fields.shellfish,

  oily_fish: dnaAssessmentSchema.fields.oily_fish,

  eggs: dnaAssessmentSchema.fields.eggs,

  poultry: dnaAssessmentSchema.fields.poultry,

  red_meat: dnaAssessmentSchema.fields.red_meat,

  pork: dnaAssessmentSchema.fields.pork,

  alcohol: dnaAssessmentSchema.fields.alcohol,
});

export const changePasswordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .trim()
    .required("Current password is required"),

  newPassword: passwordValidation,

  confirmPassword: yup
    .string()
    .trim()
    .required("Please confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

export const mfaValidation = yup.object().shape({
  otp: yup
    .string()
    .required("OTP is required")
    .matches(/^\d+$/, "OTP must contain only numbers")
    .length(6, "OTP must be exactly 6 digits"),
});

export const retestKitValidationSchema = yup.object().shape({
  first_name: nameValidation("first name"),
  last_name: nameValidation("last name"),
  ...addressFields
});
export const retestKitValidationModalSchema = yup.object().shape({
  first_name: nameValidation("first name"),
  last_name: nameValidation("last name"),
  ...addressFields,
  retest_instructions: yup
    .boolean()
    .oneOf([true], "You must agree to the sample collection instructions.")
    .required("You must agree to the sample collection instructions."),
  understand_refund_consent: yup
    .boolean()
    .oneOf([true], "You must agree to the retest refund policy.")
    .required("You must agree to the retest refund policy."),
});

